import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  DocumentTextIcon, 
  EyeIcon, 
  PencilSquareIcon, 
  TrashIcon,
  ArrowDownTrayIcon,
  ArchiveBoxIcon,
  ArrowUturnLeftIcon,
  FunnelIcon,
  PlusIcon,
  TagIcon,
  CalendarIcon,
  UserIcon,
  Squares2X2Icon,
  ListBulletIcon,
  AdjustmentsHorizontalIcon,
  SparklesIcon,
  FolderIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import { useDocumentStorage } from '../../hooks/useDocumentStorage';
import { useDocumentStorageCategories } from '../../hooks/useDocumentStorageCategories';
import { useDocumentTags } from '../../hooks/useDocumentTags';
import { useCustomers } from '../../hooks/useCustomers';
import { useAuth } from '../../contexts/AuthContext';
import { useDocumentFolders } from '../../hooks/useDocumentFolders';
import { DocumentStorage, DocumentStorageFilter, DocumentStorageStatus } from '../../types/documentStorage';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import LoadingScreen from '../common/LoadingScreen';
import Modal from '../common/Modal';
import CommonPageHeader from '../common/CommonPageHeader';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';
import DocumentStorageForm from './DocumentStorageForm';
import DocumentStorageView from './DocumentStorageView';
import DocumentStorageInfo from '../documents/DocumentStorageInfo';
import ModernDocumentGrid from './ModernDocumentGrid';
import ModernDocumentGeneratorModal from '../documents/ModernDocumentGeneratorModal';
import toast from 'react-hot-toast';

interface DocumentStorageListProps {
  customerId?: string;
  folderId?: string;
  folderIds?: string[]; // For hierarchical search
  showCustomerFilter?: boolean;
  onDocumentChange?: () => void; // Callback for when documents change
  viewMode?: 'grid' | 'list';
  status?: 'active' | 'archived'; // Document status filter
  tagCloudFilters?: {
    searchQuery: string;
    selectedTags: string[];
    selectedCategories: string[];
    selectedFolders: string[];
    dateRange: string;
    fileTypes: string[];
  };
}

export default function DocumentStorageList({ customerId, folderId, folderIds, showCustomerFilter = true, onDocumentChange, viewMode = 'list', status = 'active', tagCloudFilters }: DocumentStorageListProps) {
  const [filters, setFilters] = useState<DocumentStorageFilter>({
    customerId: customerId || undefined,
    folderId: folderId || undefined,
    folderIds: folderIds || undefined,
    status: status
  });
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<DocumentStorage | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<DocumentStorage | null>(null);
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [showDocumentGenerator, setShowDocumentGenerator] = useState(false);

  const { documents, loading, updateDocument, deleteDocument, archiveDocument, restoreDocument } = useDocumentStorage(filters);
  const { categories } = useDocumentStorageCategories();
  const { tags: documentTags, addTag: addDocumentTag } = useDocumentTags();
  const { customers } = useCustomers();
  const { user } = useAuth();
  const { folders } = useDocumentFolders();

  // Update filters when props change
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      customerId: customerId || undefined,
      folderId: folderId || undefined,
      folderIds: folderIds || undefined,
      status: status
    }));
  }, [customerId, folderId, folderIds, status]);

  // Get unique tags for filter
  const availableTags = useMemo(() => {
    const tagSet = new Set<string>();
    documents.forEach(doc => {
      doc.tags?.forEach(tag => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [documents]);

  // Apply tag cloud filters to documents
  const filteredDocuments = useMemo(() => {
    if (!tagCloudFilters) {
      return documents;
    }

    let filtered = [...documents];

    // Apply search query filter
    if (tagCloudFilters.searchQuery) {
      const query = tagCloudFilters.searchQuery.toLowerCase();
      filtered = filtered.filter(doc => 
        doc.title.toLowerCase().includes(query) ||
        doc.description?.toLowerCase().includes(query) ||
        doc.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }

    // Apply selected tags filter
    if (tagCloudFilters.selectedTags.length > 0) {
      filtered = filtered.filter(doc => {
        if (!doc.tags || doc.tags.length === 0) return false;
        // Check if any of the document's tag IDs match the selected tag names
        return doc.tags.some(tagId => {
          const tag = documentTags.find(t => t.id === tagId);
          if (!tag) return false;
          const tagName = tag.title || tag.name;
          return tagCloudFilters.selectedTags.includes(tagName);
        });
      });
    }

    // Apply selected categories filter
    if (tagCloudFilters.selectedCategories.length > 0) {
      filtered = filtered.filter(doc => 
        doc.category?.name && tagCloudFilters.selectedCategories.includes(doc.category.name)
      );
    }

    // Apply selected file types filter
    if (tagCloudFilters.fileTypes.length > 0) {
      filtered = filtered.filter(doc => {
        if (!doc.fileType) return false;
        const fileType = doc.fileType.split('/')[0]; // e.g., 'image', 'application'
        return tagCloudFilters.fileTypes.includes(fileType);
      });
    }

    // Apply date range filter
    if (tagCloudFilters.dateRange !== 'all') {
      const now = new Date();
      const filterDate = new Date();
      
      switch (tagCloudFilters.dateRange) {
        case 'today':
          filterDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          break;
        case 'year':
          filterDate.setFullYear(now.getFullYear() - 1);
          break;
      }
      
      filtered = filtered.filter(doc => {
        if (!doc.creationDate) return false;
        const docDate = doc.creationDate instanceof Date ? doc.creationDate : new Date(doc.creationDate);
        return docDate >= filterDate;
      });
    }

    return filtered;
  }, [documents, tagCloudFilters]);

  const handleEdit = (document: DocumentStorage) => {
    setSelectedDocument(document);
    setShowForm(true);
  };

  const handleView = (document: DocumentStorage) => {
    setSelectedDocument(document);
    setShowView(true);
  };

  const handleDelete = (document: DocumentStorage) => {
    setDocumentToDelete(document);
    setShowDeleteModal(true);
  };

  const handleDownload = (document: DocumentStorage) => {
    if (document.downloadUrl) {
      window.open(document.downloadUrl, '_blank');
    } else {
      toast.error('Nincs elérhető fájl a letöltéshez');
    }
  };

  const handleSelectDocument = (documentId: string) => {
    setSelectedDocuments(prev => 
      prev.includes(documentId) 
        ? prev.filter(id => id !== documentId)
        : [...prev, documentId]
    );
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedDocuments(filteredDocuments.map(doc => doc.id));
    } else {
      setSelectedDocuments([]);
    }
  };

  const confirmDelete = async () => {
    if (!documentToDelete) return;
    
    try {
      await deleteDocument(documentToDelete.id);
      setShowDeleteModal(false);
      setDocumentToDelete(null);
      toast.success('Dokumentum sikeresen törölve');
      onDocumentChange?.(); // Notify parent about document change
    } catch (error) {
      toast.error('Hiba a dokumentum törlésekor');
    }
  };

  const handleArchive = async (document: DocumentStorage) => {
    try {
      if (document.status === 'archived') {
        await restoreDocument(document.id);
      } else {
        await archiveDocument(document.id);
      }
      onDocumentChange?.(); // Notify parent about document change
    } catch (error) {
      toast.error('Hiba a művelet végrehajtásakor');
    }
  };

  const handleAddTag = async (document: DocumentStorage, tagIdOrName: string) => {
    try {
      // Check if it's an existing tag ID or a new tag name
      const existingTag = documentTags.find((t: any) => t.id === tagIdOrName);
      
      let tagId: string;
      let tagName: string;
      
      if (existingTag) {
        // It's an existing tag ID
        tagId = existingTag.id;
        tagName = existingTag.title || existingTag.name;
        
        // Check if tag already exists on document
        if (document.tags && document.tags.includes(tagId)) {
          toast.error('Ez a címke már hozzá van rendelve a dokumentumhoz!');
          return;
        }
      } else {
        // It's a new tag name, create it first
        const newTagId = await addDocumentTag(tagIdOrName);
        tagId = newTagId;
        tagName = tagIdOrName;
        
        // Check if tag already exists on document (by name for new tags)
        const existingTagByName = documentTags.find((t: any) => 
          (t.name === tagIdOrName || t.title === tagIdOrName) && 
          document.tags && document.tags.includes(t.id)
        );
        if (existingTagByName) {
          toast.error('Ez a címke már hozzá van rendelve a dokumentumhoz!');
          return;
        }
      }

      // Update document with tag ID
      const updatedTags = [...(document.tags || []), tagId];
      await updateDocument(document.id, { tags: updatedTags });
      
      // If it was an existing tag, increment its count
      if (existingTag) {
        await addDocumentTag(existingTag.name);
      }
      
      toast.success(`"${tagName}" címke hozzáadva a dokumentumhoz!`);
      onDocumentChange?.(); // Notify parent about document change
    } catch (error) {
      console.error('Error adding tag:', error);
      toast.error('Hiba a címke hozzáadásakor');
    }
  };

  const handleRemoveTag = async (document: DocumentStorage, tagId: string) => {
    try {
      const tag = documentTags.find((t: any) => t.id === tagId);
      const tagName = tag?.title || tag?.name || tagId;
      
      // Remove tag from document
      const updatedTags = (document.tags || []).filter(id => id !== tagId);
      await updateDocument(document.id, { tags: updatedTags });
      
      toast.success(`"${tagName}" címke eltávolítva a dokumentumból!`);
      onDocumentChange?.(); // Notify parent about document change
    } catch (error) {
      console.error('Error removing tag:', error);
      toast.error('Hiba a címke eltávolításakor');
    }
  };

  const handleArchiveOnly = async (document: DocumentStorage) => {
    try {
      await archiveDocument(document.id);
      toast.success('Dokumentum archiválva!');
      onDocumentChange?.();
    } catch (error) {
      console.error('Error archiving document:', error);
      toast.error('Hiba az archiválás során');
    }
  };

  const handleRestoreOnly = async (document: DocumentStorage) => {
    try {
      await restoreDocument(document.id);
      toast.success('Dokumentum visszaállítva!');
      onDocumentChange?.();
    } catch (error) {
      console.error('Error restoring document:', error);
      toast.error('Hiba a visszaállítás során');
    }
  };

  const handleBulkDelete = async (documentIds: string[]) => {
    try {
      // Delete documents one by one
      for (const docId of documentIds) {
        await deleteDocument(docId);
      }
      
      // Clear selection
      setSelectedDocuments([]);
      
      toast.success(`${documentIds.length} dokumentum sikeresen törölve`);
      onDocumentChange?.();
    } catch (error) {
      console.error('Error bulk deleting documents:', error);
      toast.error('Hiba a dokumentumok törlése során');
    }
  };

  const handleBulkDownload = async (documentIds: string[]) => {
    try {
      const documentsToDownload = filteredDocuments.filter(doc => 
        documentIds.includes(doc.id) && doc.downloadUrl
      );
      
      if (documentsToDownload.length === 0) {
        toast.error('Nincs letölthető fájl a kiválasztott dokumentumok között');
        return;
      }
      
      if (documentsToDownload.length === 1) {
        // Single document - direct download
        const doc = documentsToDownload[0];
        if (doc.downloadUrl) {
          window.open(doc.downloadUrl, '_blank');
        }
        toast.success('Dokumentum letöltése megkezdődött');
      } else {
        // Multiple documents - download with delay to avoid browser blocking
        toast.success(`${documentsToDownload.length} dokumentum letöltése megkezdődött`);
        
        documentsToDownload.forEach((doc, index) => {
          if (doc.downloadUrl) {
            setTimeout(() => {
              // Create a temporary link element for download
              const link = document.createElement('a');
              link.href = doc.downloadUrl!;
              link.download = doc.fileName || `document_${index + 1}`;
              link.target = '_blank';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }, index * 500); // 500ms delay between downloads
          }
        });
      }
    } catch (error) {
      console.error('Error bulk downloading documents:', error);
      toast.error('Hiba a dokumentumok letöltése során');
    }
  };

  // Drag & Drop handlers
  const [draggedDocument, setDraggedDocument] = useState<DocumentStorage | null>(null);
  const [dragOverDocument, setDragOverDocument] = useState<string | null>(null);
  
  // Context menu and folder selection
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [documentToMove, setDocumentToMove] = useState<DocumentStorage | null>(null);

  const handleDragStart = (e: React.DragEvent, document: DocumentStorage) => {
    setDraggedDocument(document);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', document.id);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetDocument: DocumentStorage) => {
    e.preventDefault();
    
    if (!draggedDocument || draggedDocument.id === targetDocument.id) {
      setDraggedDocument(null);
      setDragOverDocument(null);
      return;
    }

    // Ha a cél dokumentum mappában van, akkor oda mozgatjuk
    if (targetDocument.folderId) {
      handleMoveToFolder(draggedDocument, targetDocument.folderId);
    } else {
      // Ha a cél dokumentum gyökérben van, akkor gyökérbe mozgatjuk
      handleMoveToFolder(draggedDocument, null);
    }

    setDraggedDocument(null);
    setDragOverDocument(null);
  };

  const handleMove = (document: DocumentStorage) => {
    setDocumentToMove(document);
    setShowMoveModal(true);
  };

  const handleMoveToFolder = async (document: DocumentStorage, folderId: string | null) => {
    try {
      await updateDocument(document.id, { folderId: folderId || undefined });
      toast.success(`Dokumentum áthelyezve ${folderId ? 'mappába' : 'gyökérbe'}`);
      if (onDocumentChange) onDocumentChange();
    } catch (error) {
      console.error('Error moving document:', error);
      toast.error('Hiba a dokumentum áthelyezése során');
    }
  };

  const getStatusColor = (status: DocumentStorageStatus) => {
    switch (status) {
      case 'active': return '#16a34a'; // green-600 - megfelelően működő
      case 'archived': return '#94a3b8'; // slate-400 - archivált elemek
      case 'expired': return '#dc2626'; // red-600 - lejárt elemek
      case 'draft': return '#eab308'; // yellow-500 - piszkozat
      default: return '#6b7280'; // gray-500 - alapértelmezett
    }
  };

  const getStatusText = (status: DocumentStorageStatus) => {
    switch (status) {
      case 'active': return 'Aktív';
      case 'archived': return 'Archivált';
      case 'expired': return 'Lejárt';
      case 'draft': return 'Piszkozat';
      default: return status;
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-6">

        {/* Modern Document Grid/List */}
        <ModernDocumentGrid
          documents={filteredDocuments}
          loading={loading}
          viewMode={viewMode}
          selectedDocuments={selectedDocuments}
          onSelectDocument={handleSelectDocument}
          onSelectAll={handleSelectAll}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onDownload={handleDownload}
          onArchive={handleArchiveOnly}
          onRestore={handleRestoreOnly}
          onAddTag={handleAddTag}
          onRemoveTag={handleRemoveTag}
          onBulkDelete={handleBulkDelete}
          onBulkDownload={handleBulkDownload}
          onMove={handleMove}
        />

        {/* Legacy Documents Grid - Desktop (hidden) */}
        <div className="hidden">
          <Card>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Dokumentum
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Kategória
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Ügyfél
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Fájl
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Kelt dátum
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Láthatóság
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Státusz
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Storage
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Létrehozva
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Műveletek
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredDocuments.map((document) => (
                    <tr
                      key={document.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, document)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, document)}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800 border-l-4 cursor-move"
                      style={{ borderLeftColor: getStatusColor(document.status) }}
                    >
                      <td className="px-6 py-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {document.title}
                            </div>
                            {document.folderId && (
                              <div className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900 rounded-full">
                                <svg className="w-3 h-3 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                                </svg>
                                <span className="text-xs text-blue-600 dark:text-blue-400">
                                  Mappában
                                </span>
                              </div>
                            )}
                          </div>
                          {document.description && (
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {document.description}
                            </div>
                          )}
                          {document.tags && document.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {document.tags.map((tag, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200"
                                >
                                  <TagIcon className="h-3 w-3 mr-1" />
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {document.category ? (
                          <div className="flex items-center">
                            <div
                              className="w-3 h-3 rounded-full mr-2"
                              style={{ backgroundColor: document.category.color }}
                            ></div>
                            <span className="text-sm text-gray-900 dark:text-white">
                              {document.category.name}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500 dark:text-gray-400 italic">
                            Nincs kategória
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {document.customerName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {document.fileName ? (
                          <div className="text-sm">
                            {document.downloadUrl ? (
                              <a
                                href={document.downloadUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs font-mono text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 px-1.5 py-0.5 rounded cursor-pointer transition-colors"
                                title={`${document.fileName} - Kattintson a megnyitáshoz`}
                              >
                                📄 {document.fileName.length > 15 
                                  ? `${document.fileName.substring(0, 12)}...` 
                                  : document.fileName}
                              </a>
                            ) : (
                              <span 
                                className="text-xs font-mono text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 px-1.5 py-0.5 rounded"
                                title={document.fileName}
                              >
                                📄 {document.fileName.length > 15 
                                  ? `${document.fileName.substring(0, 12)}...` 
                                  : document.fileName}
                              </span>
                            )}
                            <div className="text-gray-500 dark:text-gray-400 mt-1 text-xs">
                              {formatFileSize(document.fileSize)}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            Nincs fájl
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {document.creationDate ? (
                          <div className="flex items-center">
                            <CalendarIcon className="h-4 w-4 mr-1" />
                            {document.creationDate.toLocaleDateString('hu-HU')}
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                          document.isPublic 
                            ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200' 
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                        }`}>
                          {document.isPublic ? '🌐 Nyilvános' : '🔒 Privát'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
                          style={{ 
                            backgroundColor: getStatusColor(document.status) + '20',
                            color: getStatusColor(document.status)
                          }}
                        >
                          {getStatusText(document.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <DocumentStorageInfo document={document} compact={true} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex items-center">
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          {document.createdAt.toLocaleDateString('hu-HU')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleView(document)}
                            className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
                            title="Megtekintés"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          {document.downloadUrl && (
                            <button
                              onClick={() => handleDownload(document)}
                              className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                              title="Letöltés"
                            >
                              <ArrowDownTrayIcon className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleEdit(document)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                            title="Szerkesztés"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setDocumentToMove(document);
                              setShowMoveModal(true);
                            }}
                            className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300"
                            title="Áthelyezés mappába"
                          >
                            <FolderIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleArchive(document)}
                            className="text-yellow-600 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300"
                            title={document.status === 'archived' ? 'Visszaállítás' : 'Archiválás'}
                          >
                            {document.status === 'archived' ? (
                              <ArrowUturnLeftIcon className="h-4 w-4" />
                            ) : (
                              <ArchiveBoxIcon className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleDelete(document)}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            title="Törlés"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* Documents Cards - Mobile/Tablet */}
        <div className="xl:hidden grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredDocuments.map((document) => (
            <div
              key={document.id}
              className="border-l-4 hover:shadow-md transition-shadow"
              style={{ borderLeftColor: getStatusColor(document.status) }}
            >
              <Card>
              <div className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                      {document.title}
                    </h3>
                    {document.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {document.description}
                      </p>
                    )}
                  </div>
                  <span
                    className="inline-flex px-2 py-1 text-xs font-semibold rounded-full ml-2"
                    style={{ 
                      backgroundColor: getStatusColor(document.status) + '20',
                      color: getStatusColor(document.status)
                    }}
                  >
                    {getStatusText(document.status)}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  {document.category ? (
                    <div className="flex items-center text-sm">
                      <div
                        className="w-3 h-3 rounded-full mr-2"
                        style={{ backgroundColor: document.category.color }}
                      ></div>
                      <span className="text-gray-900 dark:text-white font-medium">
                        {document.category.name}
                      </span>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500 dark:text-gray-400 italic">
                      Nincs kategória
                    </span>
                  )}

                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <strong>Ügyfél:</strong> {document.customerName}
                  </div>

                  {document.fileName && (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      <strong>Fájl:</strong> 
                      {document.downloadUrl ? (
                        <a
                          href={document.downloadUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 text-xs font-mono text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 px-1.5 py-0.5 rounded cursor-pointer transition-colors inline-block"
                          title={`${document.fileName} - Kattintson a megnyitáshoz`}
                        >
                          📄 {document.fileName.length > 20 
                            ? `${document.fileName.substring(0, 17)}...` 
                            : document.fileName}
                        </a>
                      ) : (
                        <span 
                          className="ml-2 text-xs font-mono text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 px-1.5 py-0.5 rounded inline-block"
                          title={document.fileName}
                        >
                          📄 {document.fileName.length > 20 
                            ? `${document.fileName.substring(0, 17)}...` 
                            : document.fileName}
                        </span>
                      )}
                      <span className="ml-1 text-gray-500 text-xs">({formatFileSize(document.fileSize)})</span>
                    </div>
                  )}

                  {document.creationDate && (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      <strong>Kelt dátum:</strong> {document.creationDate.toLocaleDateString('hu-HU')}
                    </div>
                  )}

                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <strong>Láthatóság:</strong> 
                    <span className={`ml-2 inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
                      document.isPublic 
                        ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200' 
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                    }`}>
                      {document.isPublic ? '🌐 Nyilvános' : '🔒 Privát'}
                    </span>
                  </div>

                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                    <CalendarIcon className="h-4 w-4 mr-1" />
                    <span className="text-xs">Létrehozva:</span> {document.createdAt.toLocaleDateString('hu-HU')}
                  </div>

                  {document.tags && document.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {document.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200"
                        >
                          <TagIcon className="h-3 w-3 mr-1" />
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => handleView(document)}
                    className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300 p-1"
                    title="Megtekintés"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </button>
                  {document.downloadUrl && (
                    <button
                      onClick={() => handleDownload(document)}
                      className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 p-1"
                      title="Letöltés"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleEdit(document)}
                    className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 p-1"
                    title="Szerkesztés"
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      setDocumentToMove(document);
                      setShowMoveModal(true);
                    }}
                    className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300 p-1"
                    title="Áthelyezés mappába"
                  >
                    <FolderIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleArchive(document)}
                    className="text-yellow-600 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300 p-1"
                    title={document.status === 'archived' ? 'Visszaállítás' : 'Archiválás'}
                  >
                    {document.status === 'archived' ? (
                      <ArrowUturnLeftIcon className="h-4 w-4" />
                    ) : (
                      <ArchiveBoxIcon className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={() => handleDelete(document)}
                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-1"
                    title="Törlés"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
              </Card>
            </div>
          ))}
        </div>

        {documents.length === 0 && (
          <Card>
            <div className="text-center py-12">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                Nincs dokumentum
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {filters.searchTerm || filters.customerId || filters.categoryId 
                  ? 'Nincs a szűrőknek megfelelő dokumentum.' 
                  : 'Kezdjen el dokumentumokat feltölteni.'
                }
              </p>
              {!customerId && (
                <div className="mt-6">
                  <Button
                    className="document-upload-button"
                    onClick={() => {
                      setSelectedDocument(null);
                      setShowForm(true);
                    }}
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Új dokumentum
                  </Button>
                </div>
              )}
            </div>
          </Card>
        )}
      </div>

      {/* Modals */}
      {showForm && (
        <Modal
          isOpen={showForm}
          onClose={() => {
            setShowForm(false);
            setSelectedDocument(null);
          }}
          title={selectedDocument ? 'Dokumentum szerkesztése' : 'Új dokumentum'}
          size="4xl"
        >
          <DocumentStorageForm
            document={selectedDocument}
            customerId={customerId}
            folderId={folderId}
            onClose={() => {
              setShowForm(false);
              setSelectedDocument(null);
              onDocumentChange?.(); // Notify parent about document change
            }}
          />
        </Modal>
      )}

      {showView && selectedDocument && (
        <Modal
          isOpen={showView}
          onClose={() => {
            setShowView(false);
            setSelectedDocument(null);
          }}
          title="Dokumentum részletek"
          size="4xl"
        >
          <DocumentStorageView
            document={selectedDocument}
            onClose={() => {
              setShowView(false);
              setSelectedDocument(null);
            }}
            onEdit={() => {
              setShowView(false);
              setShowForm(true);
            }}
          />
        </Modal>
      )}

      {/* Delete Modal */}
      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDocumentToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Dokumentum törlése"
        deleteMessage={`Biztosan törölni szeretné a "${documentToDelete?.title}" dokumentumot?`}
        deleteWarning="Ez a művelet nem vonható vissza."
      />

      {/* Document Generator Modal */}
      {showDocumentGenerator && (
        <ModernDocumentGeneratorModal
          isOpen={showDocumentGenerator}
          onClose={() => {
            setShowDocumentGenerator(false);
            onDocumentChange?.(); // Frissítjük a dokumentumlistát a generálás után
          }}
          documentDataProvider={null} // Nincs előre kiválasztott ügyfél
        />
      )}

      {/* Modern Move to Folder Modal */}
      {showMoveModal && documentToMove && (
        <Modal
          isOpen={showMoveModal}
          onClose={() => {
            setShowMoveModal(false);
            setDocumentToMove(null);
          }}
          title="Dokumentum áthelyezése"
          size="lg"
        >
          <div className="p-6">
            {/* Header with document info */}
            <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl border border-purple-100 dark:border-purple-800">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                  <DocumentTextIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">{documentToMove.title}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Válaszd ki a célmappát</p>
                </div>
              </div>
            </div>

            {/* Folder selection */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Elérhető mappák:</h4>
              
              {/* Root folder option */}
              <button
                onClick={() => {
                  handleMoveToFolder(documentToMove, null);
                  setShowMoveModal(false);
                  setDocumentToMove(null);
                }}
                className="group w-full text-left p-4 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-purple-300 dark:hover:border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all duration-200 flex items-center gap-3"
              >
                <div className="p-2 bg-gray-100 dark:bg-gray-800 group-hover:bg-purple-100 dark:group-hover:bg-purple-900/30 rounded-lg transition-colors">
                  <FolderIcon className="w-5 h-5 text-gray-600 dark:text-gray-400 group-hover:text-purple-600 dark:group-hover:text-purple-400" />
                </div>
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">Minden dokumentum</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Gyökér mappa</div>
                </div>
              </button>
              
              {/* Custom folders */}
              {folders
                .filter(folder => folder.id !== 'root')
                .map((folder) => (
                <button
                  key={folder.id}
                  onClick={() => {
                    handleMoveToFolder(documentToMove, folder.id);
                    setShowMoveModal(false);
                    setDocumentToMove(null);
                  }}
                  className="group w-full text-left p-4 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-purple-300 dark:hover:border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all duration-200 flex items-center gap-3"
                  style={{ 
                    borderLeftColor: folder.color,
                    borderLeftWidth: '6px'
                  }}
                >
                  <div 
                    className="p-2 rounded-lg transition-colors"
                    style={{ backgroundColor: `${folder.color}20` }}
                  >
                    <FolderIcon 
                      className="w-5 h-5" 
                      style={{ color: folder.color }} 
                    />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 dark:text-white">{folder.name}</div>
                    {folder.description && (
                      <div className="text-sm text-gray-500 dark:text-gray-400">{folder.description}</div>
                    )}
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-2 h-2 rounded-full bg-purple-400"></div>
                  </div>
                </button>
              ))}
            </div>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowMoveModal(false);
                    setDocumentToMove(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Mégse
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
